import moment from 'moment';
import divide from 'divide-bigint';

const dateUnitString = (value, unit) => (value ? `${value} ${unit}${value === 1 ? '' : 's'} ` : '');

export const dateDiff = (date1, date2) => {
  const diff = Math.abs(date1 - date2);
  // only include seconds if diff under a minute
  const includeSeconds = diff < (60 * 1000);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / (1000)) % 60);
  const d = days ? dateUnitString(days, 'day') : '';
  const h = hours || d ? dateUnitString(hours, 'hour') : '';
  const m = mins || h ? dateUnitString(mins, 'min') : '';
  const s = seconds || m ? dateUnitString(seconds, 'second') : '';
  return `${d}${h}${m}${includeSeconds ? s : ''}`;
};

export const not = f => (...args) => !f(...args);
export const isNull = v => v === null || typeof v === 'undefined';
export const notNull = not(isNull);
export const isArray = a => notNull(a) && Array.isArray(a);
export const isObject = o => notNull(o) && o instanceof Object && !Array.isArray(o);
export const isEmpty = v => isNull(v) || !(isObject(v) ? Object.keys(v) : `${v}`).length;
export const notEmpty = not(isEmpty);
export const isFunction = f => notNull(f) && typeof f === 'function';
export const notFunction = not(isFunction);

export const coalesce = (...args) => args.reduce((v, n) => (notNull(v) ? v : n));

export const addDaysHoursMinutes = (updates = {}, date = new Date()) =>
  Object.entries(updates).reduce((dt, [field, val]) => dt.add(val, field), moment(date));

export const parseBoolean = v =>
  ['true', 't', 'yes', 'y', '1', 'on'].includes(`${v}`.toLowerCase());
export const toArray = a => (isNull(a) ? [] : (isArray(a) ? a : [a]));
export const removeNulls = (obj = {}) =>
  JSON.parse(JSON.stringify(obj, (k, v) => (v === null ? undefined : v)));

export const queryString = (obj = {}) => Object.keys(obj)
  .filter((k) => notNull(obj[k]))
  .reduce((all, k) => (isArray(obj[k])
    ? [...all, ...obj[k].map((v) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)]
    : [...all, `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`]),
  [])
  .join('&');

export const keyComparator = (k, defVal = 0) => {
  const [key, dir] = k.split(':');
  const d = dir === 'desc' ? -1 : 1;
  return (a = {}, b = {}) => {
    const x = isNull(a[key]) ? defVal : a[key];
    const y = isNull(b[key]) ? defVal : b[key];
    return x === y ? 0 : (x < y ? -d : d);
  };
};

export const uniqueArray = (arr) => {
  const a = [];
  for (let i = 0, l = arr.length; i < l; i++) {
    if (a.indexOf(arr[i]) === -1 && arr[i] !== '') {
      a.push(arr[i]);
    }
  }
  return a;
};

export const indexArray = (arr = [], k = (e, i) => i, v = (e) => e) => arr.reduce((idx, e, i) => {
  const key = k(e, i, idx);
  idx[key] = v(e, i, idx, idx[key]);
  return idx;
}, {});

export const reverseArray = (arr = []) => arr.slice().reverse();

// Integer unless less than zero and strip redundant trailing zeros
export const prettyNumber = v => v > 1 ? `${parseInt(v, 10)}` : parseFloat(v).toString();
export const shorten = (str = '', len = 8, trunc = '') =>
  `${str.slice(0, len)}${str.length > len ? trunc : ''}`;

// Pretty with ellipsis
export const shortenAddress = (address) =>
  `${address.substr(0, 6)}...${address.substr(address.length - 4, 4)}`;

export const convertTokenValue = (v, power = 18) => {
  let result;
  try {
    // BigInt breaks if decimal places present (floats can represent up to 1.797**308)
    result = divide(Math.round(parseFloat(v)), BigInt(10 ** power));
  } catch (e) {
    console.warn(`Failed to convertTokenValue(${v}, ${power})`, e);
    result = Number.NaN;
  }
  return result;
};

export const debouncer = (delay) => {
  let timeout;
  return fn => {
    // restart delay
    clearTimeout(timeout);
    timeout = setTimeout(fn, delay);
  };
};

export const sleep = (ms = 1000) => new Promise(resolve => setTimeout(resolve, ms));
